import { t } from 'i18next';

import {
  PieceAuthProperty,
  PropertyType,
  TriggerStrategy,
} from '@activepieces/pieces-framework';
import { PieceCategory } from '@activepieces/shared';

import {
  CatalogCategory,
  KHUB_TOOLS_CATEGORY,
  OTHER_CATEGORY,
} from './catalog-categories';
import { TriggerMode } from './trigger-mode';

// Functions rather than strings: t() must run once the language is loaded, and
// Tolgee extracts keys from literal t('...') calls.
const CATEGORY_LABELS: Record<CatalogCategory, () => string> = {
  [PieceCategory.ARTIFICIAL_INTELLIGENCE]: () => t('Artificial intelligence'),
  [PieceCategory.COMMUNICATION]: () => t('Communication'),
  [PieceCategory.COMMERCE]: () => t('Commerce'),
  [PieceCategory.BUSINESS_INTELLIGENCE]: () => t('Business intelligence'),
  [PieceCategory.ACCOUNTING]: () => t('Accounting'),
  [PieceCategory.PRODUCTIVITY]: () => t('Productivity'),
  [PieceCategory.CONTENT_AND_FILES]: () => t('Content & files'),
  [PieceCategory.DEVELOPER_TOOLS]: () => t('Developer tools'),
  [PieceCategory.CUSTOMER_SUPPORT]: () => t('Customer support'),
  [PieceCategory.FORMS_AND_SURVEYS]: () => t('Forms & surveys'),
  [PieceCategory.HUMAN_RESOURCES]: () => t('Human resources'),
  [PieceCategory.PAYMENT_PROCESSING]: () => t('Payment processing'),
  [PieceCategory.MARKETING]: () => t('Marketing'),
  [PieceCategory.SALES_AND_CRM]: () => t('Sales & CRM'),
  [KHUB_TOOLS_CATEGORY]: () => t('Khub tools'),
  [OTHER_CATEGORY]: () => t('Other'),
};

export const catalogCategoryLabel = (category: CatalogCategory): string =>
  CATEGORY_LABELS[category]();

export const pieceAuthLabel = (auth: PieceAuthProperty | undefined): string => {
  switch (auth?.type) {
    case undefined:
      return t('No connection needed');
    case PropertyType.OAUTH2:
      return t('OAuth2');
    case PropertyType.SECRET_TEXT:
      return t('API key');
    case PropertyType.BASIC_AUTH:
      return t('Username & password');
    default:
      return t('Custom credentials');
  }
};

const STEP_COUNT_SEPARATOR = ' · ';

// A zero part is noise on a card: "4 actions" reads better than "0 trigger · 4 actions".
export const pieceStepCountsLabel = (
  triggerCount: number,
  actionCount: number,
): string =>
  [
    triggerCount > 0
      ? t('{count, plural, one {# trigger} other {# triggers}}', {
          count: triggerCount,
        })
      : null,
    actionCount > 0
      ? t('{count, plural, one {# action} other {# actions}}', {
          count: actionCount,
        })
      : null,
  ]
    .filter((part): part is string => part !== null)
    .join(STEP_COUNT_SEPARATOR);

export const triggerStrategyLabel = (strategy: TriggerStrategy): string =>
  strategy === TriggerStrategy.POLLING
    ? t('Checked periodically')
    : t('Instant');

const TRIGGER_MODE_LABELS: Record<TriggerMode, () => string> = {
  INSTANT: () => t('Instant'),
  PERIODIC: () => t('Checked periodically'),
  MIXED: () => t('Instant or periodic, depending on the trigger'),
};

export const triggerModeLabel = (mode: TriggerMode): string =>
  TRIGGER_MODE_LABELS[mode]();
